import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/site/Reveal";
import { EXPERTISES_DATA } from "@/data/expertises";
import { EXPERTISES_EN } from "@/data/expertises-en";
import { useLang } from "@/i18n/LanguageContext";
import { usePageMeta } from "@/lib/usePageMeta";

const CAL_URL = "https://cal.com/saber-bellaaj-5ogauj/rdv-weyb.fr";

export default function Expertises() {
    usePageMeta(
        { fr: "Expertises export — WEYB", en: "Export expertise — WEYB" },
        { fr: "Diagnostic et stratégie export, études de marché, prospection B2B & closing, salons internationaux, missions B2B.", en: "Export diagnosis & strategy, market studies, B2B prospecting & closing, international trade fairs, B2B missions." }
    );
    const { lang, t, prefix } = useLang();
    const data = lang === "en" ? EXPERTISES_EN : EXPERTISES_DATA;

    return (
        <section data-testid="expertises-page" className="bg-white pt-32 md:pt-40 pb-24 md:pb-32 min-h-screen">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Eyebrow index={t("expertisesPage.eyebrowIndex")}>{t("expertisesPage.eyebrow")}</Eyebrow>
                <Reveal>
                    <h1 className="font-display text-4xl md:text-6xl font-light tracking-tight text-weyb-ink leading-tight max-w-4xl">
                        {t("expertisesPage.title")}
                    </h1>
                </Reveal>
                <Reveal delay={0.1}>
                    <p className="mt-6 text-base md:text-lg text-gray-600 max-w-2xl leading-relaxed">
                        {t("expertisesPage.intro")}
                    </p>
                </Reveal>

                <div className="mt-16 border-t border-gray-200">
                    {data.map((e, i) => (
                        <Reveal key={e.slug} delay={i * 0.05}>
                            <Link
                                to={`${prefix}/expertises/${e.slug}`}
                                data-testid={`expertise-row-${e.slug}`}
                                className="group grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 items-center py-9 md:py-11 border-b border-gray-200 transition-colors hover:bg-[#F9FAFB] -mx-6 md:-mx-12 px-6 md:px-12"
                            >
                                <span className="md:col-span-1 font-display text-3xl font-light text-weyb-yellow">{e.num}</span>
                                <div className="md:col-span-6">
                                    <h2 className="font-display text-2xl md:text-3xl font-medium text-weyb-ink tracking-tight group-hover:translate-x-1 transition-transform">
                                        {e.title}
                                    </h2>
                                    <p className="mt-1.5 text-sm text-gray-500">{e.tagline}</p>
                                </div>
                                <p className="md:col-span-4 text-sm text-gray-500 leading-relaxed line-clamp-2">{e.intro}</p>
                                <span className="md:col-span-1 flex md:justify-end">
                                    <span className="inline-flex items-center justify-center h-11 w-11 rounded-full border border-gray-300 text-weyb-ink group-hover:bg-weyb-yellow group-hover:border-weyb-yellow transition-colors">
                                        <ArrowRight size={17} />
                                    </span>
                                </span>
                            </Link>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.15} className="mt-16">
                    <div className="bg-weyb-ink text-white rounded-sm p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
                        <div>
                            <h2 className="font-display text-2xl md:text-3xl font-light tracking-tight">
                                {t("expertisesPage.ctaTitle")}
                            </h2>
                            <p className="mt-3 text-sm text-white/60 max-w-md">
                                {t("expertisesPage.ctaText")}
                            </p>
                        </div>
                        <a
                            href={CAL_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="expertises-cta-booking"
                            className="inline-flex items-center gap-2 bg-weyb-yellow text-weyb-ink hover:bg-weyb-yellow-dark px-7 py-4 rounded-sm text-sm font-semibold transition-colors shrink-0"
                        >
                            {t("expertisesPage.ctaButton")}
                            <ArrowUpRight size={16} />
                        </a>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
